import React, { useEffect, useState } from "react";
import { apiErrorMessage, apiGet, apiPatch } from "../../api";
import SharePointFilePicker from "../../components/SharePointFilePicker";

// Settings → Master files. Where New Client's "Add Master File" puts the new
// client's folder and workbook, and which workbook it copies. T2 (corporate)
// and T1 (personal) master files live in different places with different
// templates, so each has its own pair. Saved on FirmSettings through
// /api/firm-settings/master-file/ (managers may write it).
const ENDPOINT = "/api/firm-settings/master-file/";

// Field prefixes on FirmSettings. The unprefixed set predates T1 and is T2.
const KINDS = [
  { key: "corporate", label: "T2 (corporate) clients", prefix: "master_file_" },
  { key: "personal", label: "T1 (personal) clients", prefix: "master_file_personal_" },
];

const labelStyle = {
  display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6,
};
const valueStyle = { fontSize: 14, color: "#111827" };
const mutedStyle = { fontSize: 14, color: "#9ca3af" };
const buttonStyle = {
  fontSize: 13, padding: "8px 14px", background: "#f3f4f6",
  border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 500,
};

export default function MasterFileSection() {
  const [cfg, setCfg] = useState(null);
  // { kind: "corporate" | "personal", what: "folder" | "template" } | null
  const [picking, setPicking] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    Promise.resolve()
      .then(() => apiGet(ENDPOINT))
      .then((data) => setCfg(data || {}))
      .catch(() => { setCfg({}); setError("Could not load the master file settings."); });
  }, []);

  const save = async (patch, what) => {
    setError("");
    setSaved("");
    try {
      const data = await apiPatch(ENDPOINT, patch);
      setCfg(data);
      setSaved(`${what} saved.`);
    } catch (e) {
      setError(apiErrorMessage(e, `Could not save the ${what.toLowerCase()}.`));
    }
  };

  const kindOf = (key) => KINDS.find((k) => k.key === key);

  const onFolder = (folder) => {
    const kind = kindOf(picking?.kind);
    setPicking(null);
    if (!kind) return;
    if (!folder?.driveId || !folder?.id) {
      setError("That folder could not be identified. Pick it from a SharePoint document library.");
      return;
    }
    const p = kind.prefix;
    save({
      [`${p}folder_drive_id`]: folder.driveId,
      [`${p}folder_id`]: folder.id,
      [`${p}folder_path`]: folder.path || folder.name || "",
      [`${p}folder_web_url`]: folder.webUrl || "",
    }, `${kind.label.split(" ")[0]} folder`);
  };

  const onTemplate = (file) => {
    const kind = kindOf(picking?.kind);
    setPicking(null);
    if (!kind) return;
    if (!file?.driveId || !file?.id) {
      setError("That file could not be identified. Pick it from a SharePoint document library.");
      return;
    }
    const p = kind.prefix;
    save({
      [`${p}template_drive_id`]: file.driveId,
      [`${p}template_id`]: file.id,
      [`${p}template_name`]: file.name || "",
    }, `${kind.label.split(" ")[0]} template`);
  };

  if (!cfg) return <p style={{ fontSize: 13, color: "#6b7280" }}>Loading…</p>;

  const block = (kind) => {
    const p = kind.prefix;
    const folderId = cfg[`${p}folder_id`];
    const folderPath = cfg[`${p}folder_path`];
    const folderUrl = cfg[`${p}folder_web_url`];
    const templateId = cfg[`${p}template_id`];
    const ready = Boolean(cfg[`master_file_configured_${kind.key}`]);
    return (
      <div key={kind.key} data-testid={`master-file-${kind.key}`}
           style={{ marginBottom: 24, paddingBottom: 16, borderBottom: "1px solid #e5e7eb" }}>
        <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#111827" }}>{kind.label}</h4>

        <div style={{ marginBottom: 16 }}>
          <span style={labelStyle}>Master file folder</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {folderId ? (
              folderUrl ? (
                <a href={folderUrl} target="_blank" rel="noopener noreferrer"
                   style={{ ...valueStyle, color: "#2563eb" }}>
                  {folderPath || "Selected folder"}
                </a>
              ) : (
                <span style={valueStyle}>{folderPath || "Selected folder"}</span>
              )
            ) : (
              <span style={mutedStyle}>Not chosen</span>
            )}
            <button type="button" style={buttonStyle}
                    onClick={() => setPicking({ kind: kind.key, what: "folder" })}>
              {folderId ? "Change folder" : "Choose folder"}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <span style={labelStyle}>Template workbook</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {templateId ? (
              <span style={valueStyle}>{cfg[`${p}template_name`] || "Selected file"}</span>
            ) : (
              <span style={mutedStyle}>Not chosen</span>
            )}
            <button type="button" style={buttonStyle}
                    onClick={() => setPicking({ kind: kind.key, what: "template" })}>
              {templateId ? "Change template" : "Choose template"}
            </button>
          </div>
        </div>

        <p style={{ fontSize: 13, margin: 0, color: ready ? "#065f46" : "#92400e" }}>
          {ready
            ? `Set up: New Client will offer Add Master File for ${kind.label}.`
            : "Not set up: choose both a folder and a template."}
        </p>
      </div>
    );
  };

  const pickingKind = kindOf(picking?.kind);

  return (
    <div>
      <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0, marginBottom: 20 }}>
        When a client is created with <strong>Add Master File</strong> ticked, a folder
        named after the client is made in the folder below, and a copy of the template
        is put inside it as "Master - &lt;client name&gt;.xlsx" and linked to the
        client. Each person uses their own SharePoint connection, so they need edit
        access to these folders.
      </p>

      {KINDS.map(block)}

      {saved && <p style={{ fontSize: 13, color: "#065f46", marginBottom: 0 }}>{saved}</p>}
      {error && <p role="alert" style={{ fontSize: 13, color: "#b91c1c", marginBottom: 0 }}>{error}</p>}

      <SharePointFilePicker
        isOpen={picking?.what === "folder"}
        mode="folder"
        title={`Choose the ${pickingKind?.label || ""} master file folder`}
        subtitle="Open the folder in a SharePoint library, then Select this folder"
        onClose={() => setPicking(null)}
        onFolderSelected={onFolder}
      />
      <SharePointFilePicker
        isOpen={picking?.what === "template"}
        title={`Choose the ${pickingKind?.label || ""} master file template`}
        subtitle="The blank workbook every new master file is copied from"
        onClose={() => setPicking(null)}
        onFileSelected={onTemplate}
      />
    </div>
  );
}
